import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
    Plus, Building2, MoreVertical, Trash2, X, Edit2,
    RefreshCw, Copy, Users, LayoutGrid, ShieldCheck, Mail, Lock, Folder
} from 'lucide-react';
import { Organization } from '../types';
import { apiUrl } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const OrganizationsPage: React.FC = () => {
    const { token } = useAuth();
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState<'new' | 'edit' | null>(null);
    const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
    const [createAdmin, setCreateAdmin] = useState(true);
    const [orgMembers, setOrgMembers] = useState<any[]>([]);
    const [loadingMembers, setLoadingMembers] = useState(false);
    const [activeTab, setActiveTab] = useState<'members' | 'workspaces'>('workspaces');

    // Workspace management state
    const [orgWorkspaces, setOrgWorkspaces] = useState<any[]>([]);
    const [allWorkspaces, setAllWorkspaces] = useState<any[]>([]);
    const [loadingWorkspaces, setLoadingWorkspaces] = useState(false);
    const [showWsPicker, setShowWsPicker] = useState(false);
    const [wsPickerSearch, setWsPickerSearch] = useState('');

    // Form State
    const [formData, setFormData] = useState({
        name: '',
        plan: 'Starter',
        max_workspaces: 3,
        max_users: 50,
        admin_name: '',
        admin_email: '',
        admin_password: ''
    });

    const [editingOrgId, setEditingOrgId] = useState<string | null>(null);

    useEffect(() => {
        fetchOrganizations();
    }, [token]);

    const fetchOrganizations = async () => {
        if (!token) return;
        setLoading(true);
        try {
            const response = await fetch(apiUrl('/api/auth/organizations'), {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setOrganizations(data);
            }
        } catch (error) {
            console.error('Failed to fetch organizations:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async () => {
        if (!token) return;

        // Basic validation
        if (showModal === 'new' && createAdmin && formData.admin_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.admin_email)) {
            alert('Please enter a valid email address.');
            return;
        }

        if (showModal === 'new' && createAdmin && (!formData.admin_email || !formData.admin_password)) {
            alert('Admin email and password are required when creating an admin user.');
            return;
        }

        try {
            let url = apiUrl('/api/auth/organizations');
            let method = 'POST';

            if (showModal === 'edit' && editingOrgId) {
                url = apiUrl(`/api/auth/organizations/${editingOrgId}`);
                method = 'PUT';
            }

            const submissionData = {
                ...formData,
                admin_name: createAdmin ? formData.admin_name : null,
                admin_email: createAdmin ? formData.admin_email : null,
                admin_password: createAdmin ? formData.admin_password : null,
            };

            const response = await fetch(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(submissionData)
            });

            if (response.ok) {
                await fetchOrganizations();
                resetForm();
            } else {
                const error = await response.json();
                let errorMessage = error.detail || 'Failed to save organization';
                if (Array.isArray(errorMessage)) {
                    errorMessage = errorMessage.map((e: any) => e.msg).join('\n');
                }
                alert(errorMessage);
            }
        } catch (error) {
            console.error('Failed to save organization:', error);
        }
    };

    const handleEdit = (org: Organization) => {
        setFormData({
            name: org.name,
            plan: org.plan,
            max_workspaces: org.max_workspaces,
            max_users: org.max_users,
            admin_name: '', // Not editable
            admin_email: '', // Not editable
            admin_password: '' // Not editable
        });
        setEditingOrgId(org.id);
        setShowModal('edit');
        setActiveMenuId(null);
        setActiveTab('workspaces');
        fetchOrgWorkspaces(org.id);
    };

    const fetchOrgMembers = async (orgId: string) => {
        if (!token) return;
        setLoadingMembers(true);
        try {
            const response = await fetch(apiUrl(`/api/auth/organizations/${orgId}/members`), {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setOrgMembers(data);
            }
        } catch (error) {
            console.error('Failed to fetch org members:', error);
        } finally {
            setLoadingMembers(false);
        }
    };

    const handleUpdateMemberRole = async (userId: string, newRole: string) => {
        if (!token || !editingOrgId) return;
        try {
            const response = await fetch(apiUrl(`/api/auth/organizations/${editingOrgId}/members/${userId}`), {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ role_in_org: newRole.toLowerCase() })
            });
            if (response.ok) {
                await fetchOrgMembers(editingOrgId);
            } else {
                const error = await response.json();
                alert(error.detail || 'Failed to update member role');
            }
        } catch (error) {
            console.error('Failed to update member role:', error);
        }
    };

    const handleRemoveMember = async (userId: string) => {
        if (!token || !editingOrgId || !window.confirm('Are you sure you want to remove this user from the organization?')) return;
        try {
            const response = await fetch(apiUrl(`/api/auth/organizations/${editingOrgId}/members/${userId}`), {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                await fetchOrgMembers(editingOrgId);
            } else {
                alert('Failed to remove member');
            }
        } catch (error) {
            console.error('Failed to remove member:', error);
        }
    };

    const handleDelete = async (orgId: string) => {
        if (!token || !window.confirm('Are you sure you want to delete this organization? This action cannot be undone.')) return;

        try {
            const response = await fetch(apiUrl(`/api/auth/organizations/${orgId}`), {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                await fetchOrganizations();
            } else {
                alert('Failed to delete organization');
            }
        } catch (error) {
            console.error('Error deleting organization:', error);
        }
    };

    const handleRegenerateCode = async (orgId: string) => {
        if (!token) return;
        try {
            const response = await fetch(apiUrl(`/api/auth/organizations/${orgId}/regenerate-invite-code`), {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                await fetchOrganizations();
                setActiveMenuId(null);
            }
        } catch (error) {
            console.error('Failed to regenerate code:', error);
        }
    };

    const fetchOrgWorkspaces = async (orgId: string) => {
        if (!token) return;
        setLoadingWorkspaces(true);
        try {
            const response = await fetch(apiUrl('/api/workspaces/'), {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setAllWorkspaces(data);
                setOrgWorkspaces(data.filter((ws: any) => ws.organization_id === orgId));
            }
        } catch (error) {
            console.error('Failed to fetch org workspaces:', error);
        } finally {
            setLoadingWorkspaces(false);
        }
    };

    const handleAssignWorkspaceToOrg = async (wsId: string) => {
        if (!token || !editingOrgId) return;
        try {
            const response = await fetch(apiUrl(`/api/workspaces/${wsId}`), {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ organization_id: editingOrgId })
            });
            if (response.ok) {
                setShowWsPicker(false);
                setWsPickerSearch('');
                await fetchOrgWorkspaces(editingOrgId);
                await fetchOrganizations();
            } else {
                const err = await response.json();
                alert(err.detail || 'Failed to assign workspace');
            }
        } catch (error) {
            console.error('Failed to assign workspace:', error);
        }
    };

    const handleDeleteOrgWorkspace = async (wsId: string) => {
        if (!token || !window.confirm('Are you sure you want to delete this workspace? This cannot be undone.')) return;
        try {
            const response = await fetch(apiUrl(`/api/workspaces/${wsId}`), {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                if (editingOrgId) {
                    await fetchOrgWorkspaces(editingOrgId);
                    await fetchOrganizations();
                }
            } else {
                alert('Failed to delete workspace');
            }
        } catch (error) {
            console.error('Failed to delete workspace:', error);
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            plan: 'Starter',
            max_workspaces: 3,
            max_users: 50,
            admin_name: '',
            admin_email: '',
            admin_password: ''
        });
        setEditingOrgId(null);
        setShowModal(null);
        setCreateAdmin(true);
        setOrgMembers([]);
        setOrgWorkspaces([]);
        setAllWorkspaces([]);
        setShowWsPicker(false);
        setWsPickerSearch('');
        setActiveTab('workspaces');
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        // Could replace with a toast
    };

    return (
        <div className="space-y-8 relative pb-20">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Organizations</h1>
                    <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">Manage client organizations, plans, and access.</p>
                </div>
                <button
                    onClick={() => setShowModal('new')}
                    className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-bold hover:bg-blue-700 shadow-lg shadow-blue-500/20 transition-all active:scale-95"
                >
                    <Plus size={18} /> New Organization
                </button>
            </div>

            {/* Cards Grid */}
            {/* Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {organizations.map((org) => (
                    <div key={org.id} className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 flex flex-col shadow-sm hover:shadow-md transition-all group hover:border-blue-200 dark:hover:border-blue-900/40">
                        {/* Card Header */}
                        <div className="flex items-start justify-between mb-4">
                            <div className="flex items-start gap-4 w-full">
                                <div className="p-2.5 bg-slate-50 dark:bg-slate-800 rounded-xl text-slate-600 dark:text-slate-400 border border-slate-100 dark:border-slate-700 shrink-0">
                                    <Building2 size={20} />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center justify-between gap-2 mb-1">
                                        <h3 className="font-bold text-slate-900 dark:text-slate-100 text-lg leading-tight truncate pr-2">{org.name}</h3>
                                        <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded-full border ${org.plan === 'Enterprise'
                                            ? 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/20 dark:text-purple-300 dark:border-purple-800'
                                            : org.plan === 'Pro'
                                                ? 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800'
                                                : 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700'
                                            }`}>
                                            {org.plan}
                                        </span>
                                    </div>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 font-mono truncate">org_{org.name.toLowerCase().replace(/\s+/g, '_')}.com</p>
                                </div>
                            </div>
                        </div>

                        {/* Divider */}
                        <div className="h-px bg-slate-100 dark:bg-slate-800 my-4"></div>

                        {/* Stats - Clean Row */}
                        <div className="grid grid-cols-2 gap-4 mb-6">
                            <div>
                                <div className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Users</div>
                                <div className="flex items-baseline gap-1">
                                    <span className="text-xl font-bold text-slate-900 dark:text-slate-100">{org.user_count}</span>
                                    <span className="text-sm text-slate-400 dark:text-slate-600">/{org.max_users}</span>
                                </div>
                                <div className="w-full h-1 bg-slate-100 dark:bg-slate-800 rounded-full mt-2 overflow-hidden">
                                    <div
                                        className="h-full bg-blue-500 rounded-full"
                                        style={{ width: `${Math.min((org.user_count / org.max_users) * 100, 100)}%` }}
                                    ></div>
                                </div>
                            </div>
                            <div>
                                <div className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Workspaces</div>
                                <div className="flex items-baseline gap-1">
                                    <span className="text-xl font-bold text-slate-900 dark:text-slate-100">{org.workspace_count}</span>
                                    <span className="text-sm text-slate-400 dark:text-slate-600">/{org.max_workspaces}</span>
                                </div>
                                <div className="w-full h-1 bg-slate-100 dark:bg-slate-800 rounded-full mt-2 overflow-hidden">
                                    <div
                                        className="h-full bg-emerald-500 rounded-full"
                                        style={{ width: `${Math.min((org.workspace_count / org.max_workspaces) * 100, 100)}%` }}
                                    ></div>
                                </div>
                            </div>
                        </div>

                        {/* Invite Code - Minimal */}
                        <div className="mb-6 bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3 border border-slate-100 dark:border-slate-800">
                            <div className="flex items-center justify-between">
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">INVITE CODE</span>
                                <div className="flex gap-1">
                                    <button
                                        onClick={() => copyToClipboard(org.invite_code)}
                                        className="p-1 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
                                        title="Copy Code"
                                    >
                                        <Copy size={12} />
                                    </button>
                                    <button
                                        onClick={() => handleRegenerateCode(org.id)}
                                        className="p-1 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
                                        title="Regenerate Code"
                                    >
                                        <RefreshCw size={12} />
                                    </button>
                                </div>
                            </div>
                            <div className="mt-1 font-mono text-sm font-semibold text-slate-700 dark:text-slate-300 tracking-wider select-all">
                                {org.invite_code}
                            </div>
                        </div>

                        {/* Footer Actions - Subtle */}
                        <div className="mt-auto grid grid-cols-2 gap-3">
                            <button
                                onClick={() => handleEdit(org)}
                                className="flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 border border-transparent hover:border-slate-200 dark:hover:border-slate-700 transition-all"
                            >
                                <Edit2 size={14} /> Edit
                            </button>
                            <button
                                onClick={() => handleDelete(org.id)}
                                className="flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10 border border-transparent hover:border-red-100 dark:hover:border-red-900/20 transition-all opacity-80 hover:opacity-100"
                            >
                                <Trash2 size={14} /> Delete
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* Create Modal */}
            {showModal && createPortal(
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-slate-900 rounded-3xl w-full max-w-4xl shadow-2xl animate-fadeIn overflow-hidden flex flex-col max-h-[90vh] border border-slate-200 dark:border-slate-800">
                        {/* Modal Header */}
                        <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-white dark:bg-slate-900 sticky top-0 z-10">
                            <div>
                                <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">{showModal === 'new' ? 'Create New Organization' : 'Update Organization'}</h2>
                                <p className="text-slate-500 dark:text-slate-400 text-sm">Set up organization details and initial access.</p>
                            </div>
                            <button onClick={resetForm} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl text-slate-400 transition-colors">
                                <X size={24} />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-8 overflow-y-auto">
                            <div className="grid md:grid-cols-2 gap-12">
                                {/* Left Col: Org Details */}
                                <div className="space-y-6">
                                    <div className="flex items-center gap-3 text-blue-600 mb-2">
                                        <Building2 size={20} />
                                        <h3 className="font-bold text-sm uppercase tracking-wider">Organization Details</h3>
                                    </div>

                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Organization Name <span className="text-red-500">*</span></label>
                                            <input
                                                type="text"
                                                value={formData.name}
                                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                                placeholder="e.g. Microsoft India"
                                                className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all text-slate-900 dark:text-slate-100"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Plan</label>
                                            <select
                                                value={formData.plan}
                                                onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
                                                className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all appearance-none text-slate-900 dark:text-slate-100"
                                            >
                                                <option value="Starter" className="dark:bg-slate-900">Starter</option>
                                                <option value="Pro" className="dark:bg-slate-900">Pro</option>
                                                <option value="Enterprise" className="dark:bg-slate-900">Enterprise</option>
                                            </select>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Max Workspaces</label>
                                                <input
                                                    type="number"
                                                    value={formData.max_workspaces}
                                                    onChange={(e) => setFormData({ ...formData, max_workspaces: parseInt(e.target.value) })}
                                                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all text-slate-900 dark:text-slate-100"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Max Users</label>
                                                <input
                                                    type="number"
                                                    value={formData.max_users}
                                                    onChange={(e) => setFormData({ ...formData, max_users: parseInt(e.target.value) })}
                                                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all text-slate-900 dark:text-slate-100"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Right Col: Admin Details - ONLY FOR NEW ORG */}
                                {showModal === 'new' && (
                                    <div className="space-y-6">
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center gap-3 text-green-600">
                                                <ShieldCheck size={20} />
                                                <h3 className="font-bold text-sm uppercase tracking-wider">First Admin User</h3>
                                            </div>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    className="sr-only peer"
                                                    checked={createAdmin}
                                                    onChange={(e) => setCreateAdmin(e.target.checked)}
                                                />
                                                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-slate-600 peer-checked:bg-blue-600"></div>
                                                <span className="ml-3 text-sm font-medium text-slate-700 dark:text-slate-300">{createAdmin ? 'Enabled' : 'Disabled'}</span>
                                            </label>
                                        </div>

                                        {createAdmin ? (
                                            <div className="space-y-4 animate-fadeIn">
                                                <div>
                                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Admin Name</label>
                                                    <div className="relative">
                                                        <Users className="absolute left-3.5 top-3 text-slate-400" size={16} />
                                                        <input
                                                            type="text"
                                                            value={formData.admin_name}
                                                            onChange={(e) => setFormData({ ...formData, admin_name: e.target.value })}
                                                            placeholder="e.g. Anmol Sharma"
                                                            className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                                                        />
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Admin Email <span className="text-red-500">*</span></label>
                                                    <div className="relative">
                                                        <Mail className="absolute left-3.5 top-3 text-slate-400" size={16} />
                                                        <input
                                                            type="email"
                                                            value={formData.admin_email}
                                                            onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })}
                                                            placeholder="admin@microsoft.com"
                                                            className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                                                        />
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Admin Password <span className="text-red-500">*</span></label>
                                                    <div className="relative">
                                                        <Lock className="absolute left-3.5 top-3 text-slate-400" size={16} />
                                                        <input
                                                            type="password"
                                                            value={formData.admin_password}
                                                            onChange={(e) => setFormData({ ...formData, admin_password: e.target.value })}
                                                            placeholder="Min 6 characters"
                                                            className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                                                        />
                                                    </div>
                                                </div>

                                                <div className="flex items-start gap-3 p-4 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                                                    <ShieldCheck size={16} className="shrink-0 mt-0.5 text-slate-400" />
                                                    <p>This user will be the Super Admin of the new organization and can manage all settings, users, and workspaces.</p>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="flex flex-col items-center justify-center p-8 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-dashed border-slate-200 dark:border-slate-700 text-center animate-fadeIn">
                                                <div className="w-12 h-12 bg-white dark:bg-slate-800 rounded-full flex items-center justify-center text-slate-400 mb-3 shadow-sm">
                                                    <Users size={24} />
                                                </div>
                                                <h4 className="text-sm font-bold text-slate-900 dark:text-slate-100 mb-1">No Admin User</h4>
                                                <p className="text-xs text-slate-500 dark:text-slate-400 max-w-[200px]">Organization will be created without an initial user.</p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Workspaces Panel for Edit Mode */}
                                {showModal === 'edit' && (
                                    <div className="space-y-5">
                                        <div className="flex items-center gap-3 text-blue-600 mb-2">
                                            <Folder size={18} />
                                            <h3 className="font-bold text-sm uppercase tracking-wider">Workspaces</h3>
                                        </div>

                                        {/* Workspaces */}
                                        <div className="space-y-4">
                                            {/* Add workspace button + picker */}
                                            <div className="space-y-2">
                                                <button
                                                    onClick={() => setShowWsPicker(!showWsPicker)}
                                                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-xs font-bold rounded-xl hover:bg-blue-700 shadow-md shadow-blue-500/20 transition-all active:scale-95 w-full justify-center"
                                                >
                                                    <Plus size={14} /> Add Workspace
                                                </button>

                                                {showWsPicker && (() => {
                                                    const available = allWorkspaces.filter(
                                                        ws => !orgWorkspaces.some(ow => ow.id === ws.id)
                                                    ).filter(ws =>
                                                        ws.name.toLowerCase().includes(wsPickerSearch.toLowerCase())
                                                    );
                                                    return (
                                                        <div className="bg-white dark:bg-slate-800 border border-blue-100 dark:border-blue-900/30 rounded-2xl shadow-lg overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                                                            <div className="p-3 border-b border-slate-100 dark:border-slate-700">
                                                                <input
                                                                    type="text"
                                                                    value={wsPickerSearch}
                                                                    onChange={(e) => setWsPickerSearch(e.target.value)}
                                                                    placeholder="Search workspaces..."
                                                                    autoFocus
                                                                    className="w-full px-3 py-1.5 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:border-blue-500 text-slate-900 dark:text-slate-100"
                                                                />
                                                            </div>
                                                            <div className="max-h-48 overflow-y-auto">
                                                                {available.length === 0 ? (
                                                                    <div className="p-4 text-center text-sm text-slate-400">
                                                                        {allWorkspaces.length === 0 ? 'No workspaces available' : 'All workspaces already added'}
                                                                    </div>
                                                                ) : (
                                                                    available.map(ws => (
                                                                        <button
                                                                            key={ws.id}
                                                                            onClick={() => handleAssignWorkspaceToOrg(ws.id)}
                                                                            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors text-left"
                                                                        >
                                                                            <div className="w-7 h-7 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0">
                                                                                <Folder size={13} />
                                                                            </div>
                                                                            <div>
                                                                                <div className="text-sm font-bold text-slate-800 dark:text-slate-100 leading-tight">{ws.name}</div>
                                                                                <div className="text-[10px] text-slate-400 uppercase tracking-wide">{ws.status || 'Active'}</div>
                                                                            </div>
                                                                        </button>
                                                                    ))
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })()}
                                            </div>

                                            {/* Workspaces list */}
                                            <div className="bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden text-slate-900 dark:text-slate-100">
                                                {loadingWorkspaces ? (
                                                    <div className="p-8 text-center text-slate-500">
                                                        <RefreshCw className="animate-spin inline-block mr-2" size={18} />
                                                        Loading workspaces...
                                                    </div>
                                                ) : orgWorkspaces.length === 0 ? (
                                                    <div className="p-8 text-center text-slate-400">
                                                        <Folder size={30} className="mx-auto mb-2 opacity-30" />
                                                        <p className="text-sm font-bold">No workspaces yet</p>
                                                        <p className="text-[10px] mt-1 uppercase tracking-wider">Create one above</p>
                                                    </div>
                                                ) : (
                                                    <div className="divide-y divide-slate-100 dark:divide-slate-800">
                                                        {orgWorkspaces.map((ws) => (
                                                            <div key={ws.id} className="flex items-center justify-between px-4 py-3 hover:bg-slate-100 dark:hover:bg-slate-800/50 transition-colors">
                                                                <div className="flex items-center gap-3">
                                                                    <div className="w-8 h-8 rounded-xl bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0">
                                                                        <Folder size={15} />
                                                                    </div>
                                                                    <div>
                                                                        <div className="text-sm font-bold text-slate-900 dark:text-slate-100 leading-tight">{ws.name}</div>
                                                                        {ws.description && (
                                                                            <div className="text-[10px] text-slate-400 truncate max-w-[200px]">{ws.description}</div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-center gap-3 shrink-0">
                                                                    <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${ws.status === 'Active'
                                                                        ? 'bg-emerald-50 text-emerald-600 border-emerald-100 dark:bg-emerald-900/20 dark:border-emerald-900/30'
                                                                        : 'bg-slate-100 text-slate-400 border-slate-200 dark:bg-slate-800 dark:border-slate-700'
                                                                        }`}>{ws.status}</span>
                                                                    <button
                                                                        onClick={() => handleDeleteOrgWorkspace(ws.id)}
                                                                        className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-lg transition-colors"
                                                                        title="Delete Workspace"
                                                                    >
                                                                        <Trash2 size={15} />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="p-6 bg-slate-50 dark:bg-slate-800 border-t border-slate-100 dark:border-slate-700 flex justify-end gap-3 sticky bottom-0 z-10">
                            <button
                                onClick={resetForm}
                                className="px-6 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-xl text-sm font-bold hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCreate}
                                disabled={!formData.name || (createAdmin && (!formData.admin_email || !formData.admin_password) && showModal === 'new')}
                                className="px-6 py-2.5 bg-green-500 text-white rounded-xl text-sm font-bold hover:bg-green-600 shadow-lg shadow-green-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {showModal === 'new' ? (createAdmin ? 'Create Organization & Admin' : 'Create Organization') : 'Update Organization'}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default OrganizationsPage;
